import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Search, Loader2, FileText, Eye, Send, CheckCircle, XCircle, PenTool, Trash2, Copy } from "lucide-react";
import { TableRowsSkeleton } from "../components/skeletons";
import { toast } from "sonner";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700", icon: Send },
  viewed: { label: "Viewed", color: "bg-purple-100 text-purple-700", icon: Eye },
  approved: { label: "Approved", color: "bg-amber-100 text-amber-700", icon: CheckCircle },
  declined: { label: "Declined", color: "bg-red-100 text-red-700", icon: XCircle },
  signed: { label: "Signed", color: "bg-emerald-100 text-emerald-700", icon: PenTool },
};

export default function Quotes() {
  const navigate = useNavigate();
  const { 
    quotes, 
    fetchQuotes, 
    deleteQuote,
    contacts,
    fetchContacts,
    projects,
    fetchProjects,
    loading 
  } = useData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    fetchQuotes();
    fetchContacts();
    fetchProjects();
  }, [fetchQuotes, fetchContacts, fetchProjects]);

  const filteredQuotes = quotes.filter(quote => {
    const contact = contacts.find(c => c.id === quote.contact_id);
    const project = projects.find(p => p.id === quote.project_id);
    
    const matchesSearch = 
      quote.quote_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project?.project_address?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || quote.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getContactName = (contactId) => contacts.find(c => c.id === contactId)?.name || "Unknown";
  const getProjectAddress = (projectId) => projects.find(p => p.id === projectId)?.project_address || "No address";

  return (
    <div className="min-h-screen">
      <Header title="Quotes">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
          onClick={() => navigate("/quotes/new")}
          data-testid="create-quote-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Quote
        </Button>
      </Header>

      <div className="p-6">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search quotes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-quotes"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-48 h-12">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                <SelectItem key={value} value={value}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Quotes Table */}
        {loading ? (
          <TableRowsSkeleton rows={6} cols={5} />
        ) : filteredQuotes.length > 0 ? (
          <>
            {/* Mobile: card list — no horizontal scroll. */}
            <div className="sm:hidden space-y-2" data-testid="quotes-mobile-list">
              {filteredQuotes.map((quote) => {
                const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
                const StatusIcon = statusConfig.icon;
                return (
                  <button
                    key={quote.id}
                    type="button"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                    className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 active:bg-slate-50"
                    data-testid={`quote-card-${quote.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#0F4C81]">{quote.quote_number}</p>
                        <p className="text-sm text-slate-800 truncate mt-0.5">{getContactName(quote.contact_id)}</p>
                        <p className="text-xs text-slate-500 truncate">{getProjectAddress(quote.project_id)}</p>
                      </div>
                      <Badge className={`${statusConfig.color} flex items-center gap-1 flex-shrink-0`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Quote #</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Project</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Created</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote) => {
                  const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr 
                      key={quote.id}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                      data-testid={`quote-row-${quote.id}`}
                    >
                      <td className="px-4 py-4">
                        <span className="font-medium text-[#0F4C81]">{quote.quote_number}</span>
                      </td>
                      <td className="px-4 py-4 text-slate-700">
                        {getContactName(quote.contact_id)}
                      </td>
                      <td className="px-4 py-4 text-slate-600 max-w-[200px] truncate">
                        {getProjectAddress(quote.project_id)}
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-slate-500 text-sm">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-[#0F4C81] hover:bg-blue-50 h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              const token = localStorage.getItem("token");
                              axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${quote.id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } })
                                .then(() => { toast.success("Quote duplicated"); fetchQuotes(); })
                                .catch(() => toast.error("Failed to duplicate"));
                            }}
                            data-testid={`duplicate-quote-${quote.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {confirmDeleteId === quote.id ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteQuote(quote.id).then(() => { toast.success("Moved to trash"); setConfirmDeleteId(null); }).catch(() => toast.error("Failed to delete quote"));
                                }}
                                data-testid={`confirm-delete-quote-${quote.id}`}
                              >
                                Delete
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteId(quote.id);
                              }}
                              data-testid={`delete-quote-${quote.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchQuery || filterStatus !== "all" ? "No quotes found" : "No quotes yet"}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || filterStatus !== "all" 
                ? "Try different search or filters" 
                : "Create your first quote to get started"}
            </p>
            <Button
              className="bg-[#0F4C81] hover:bg-[#0D4270]"
              onClick={() => navigate("/quotes/new")}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Quote
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
